import React, { useState, useEffect } from 'react';
import workOrdersService from '../../services/workOrdersService';
import './WorkOrderDetailsModal.css';

const STATUS_CONFIG = {
  'Draft': { color: '#6b7280', icon: '📝' },
  'Submitted': { color: '#6366f1', icon: '📥' },
  'In Production': { color: '#f59e0b', icon: '🖨️' },
  'Completed': { color: '#22c55e', icon: '✅' },
  'Delivered': { color: '#3b82f6', icon: '📦' }
};

const PRIORITY_CONFIG = {
  rush: { color: '#ef4444', bg: '#fef2f2', label: 'Rush' },
  high: { color: '#f59e0b', bg: '#fffbeb', label: 'High' },
  normal: { color: '#3b82f6', bg: '#eff6ff', label: 'Normal' },
  low: { color: '#6b7280', bg: '#f9fafb', label: 'Low' }
};

const PRINT_LOCATIONS = [
  { key: 'front', label: 'Front' },
  { key: 'back', label: 'Back' },
  { key: 'sleeve_r', label: 'Sleeve R' },
  { key: 'sleeve_l', label: 'Sleeve L' },
  { key: 'heart', label: 'Heart' },
  { key: 'on_pocket', label: 'On Pocket' }
];

const WorkOrderDetailsModal = ({ job, isOpen, onClose, onSuccess, onEdit, canAdmin, statuses }) => {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [statusChangeNotes, setStatusChangeNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch history when job changes
  useEffect(() => {
    const fetchHistory = async () => {
      if (!job?.id) return;
      setLoadingHistory(true);
      try {
        const response = await workOrdersService.getHistory(job.id);
        setHistory(response.history || []);
      } catch (err) {
        console.error('Error fetching history:', err);
      } finally {
        setLoadingHistory(false);
      }
    };

    if (isOpen && job) {
      fetchHistory();
      setNewStatus(job.status);
    }
  }, [job, isOpen]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return '$' + parseFloat(amount).toFixed(2);
  };

  const handleStatusChange = async () => {
    if (!newStatus || newStatus === job.status) return;
    setChangingStatus(true);
    try {
      await workOrdersService.updateStatus(job.id, newStatus, statusChangeNotes);
      setStatusChangeNotes('');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error changing status:', err);
      alert(err.response?.data?.message || 'Failed to update status');
    } finally {
      setChangingStatus(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await workOrdersService.downloadPdf(job.id, job.work_order_number);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      await workOrdersService.duplicateWorkOrder(job.id);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error duplicating:', err);
      alert(err.response?.data?.message || 'Failed to duplicate work order');
    } finally {
      setDuplicating(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await workOrdersService.deleteWorkOrder(job.id);
      setShowDeleteConfirm(false);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error deleting:', err);
      alert(err.response?.data?.message || 'Failed to delete work order');
    } finally {
      setDeleting(false);
    }
  };

  const getActivePrintLocations = () => {
    if (!job?.print_locations) return [];
    const locations = typeof job.print_locations === 'string'
      ? JSON.parse(job.print_locations)
      : job.print_locations;
    return PRINT_LOCATIONS.filter(loc => locations[loc.key]);
  };

  if (!isOpen || !job) return null;

  const statusConfig = STATUS_CONFIG[job.status] || { color: '#6b7280', icon: '•' };
  const priorityConfig = PRIORITY_CONFIG[job.priority] || PRIORITY_CONFIG.normal;

  return (
    <div className="wo-details-overlay active" onClick={onClose}>
      <div className="wo-details-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="wo-details-header">
          <div className="wo-details-title">
            <span className="wo-details-number">{job.work_order_number}</span>
            <div className="wo-details-badges">
              <span
                className="wo-details-status-badge"
                style={{ backgroundColor: statusConfig.color }}
              >
                <span>{statusConfig.icon}</span>
                <span>{job.status}</span>
              </span>
              <span
                className="wo-details-priority-badge"
                style={{ backgroundColor: priorityConfig.bg, color: priorityConfig.color }}
              >
                {priorityConfig.label}
              </span>
            </div>
          </div>
          <button className="wo-details-close" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="wo-details-body">
          {/* Actions Row */}
          <div className="wo-details-actions">
            <button className="wo-action-btn wo-action-pdf" onClick={handleDownloadPdf} disabled={downloadingPdf}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="12" y1="18" x2="12" y2="12"/>
                <polyline points="9 15 12 18 15 15"/>
              </svg>
              {downloadingPdf ? 'Downloading...' : 'Download PDF'}
            </button>
            {canAdmin && (
              <>
                <button className="wo-action-btn" onClick={() => onEdit(job)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Edit
                </button>
                <button className="wo-action-btn" onClick={handleDuplicate} disabled={duplicating}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  {duplicating ? 'Duplicating...' : 'Duplicate'}
                </button>
                <button className="wo-action-btn wo-action-delete" onClick={() => setShowDeleteConfirm(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                  Delete
                </button>
              </>
            )}
          </div>

          {/* Main Info Grid */}
          <div className="wo-details-grid">
            <div className="wo-details-section">
              <h4>Customer Information</h4>
              <div className="wo-info-row">
                <span className="wo-info-label">Customer</span>
                <span className="wo-info-value wo-info-customer">{job.customer}</span>
              </div>
              <div className="wo-info-row">
                <span className="wo-info-label">Phone</span>
                <span className="wo-info-value">{job.contact_phone || '-'}</span>
              </div>
              <div className="wo-info-row">
                <span className="wo-info-label">Email</span>
                <span className="wo-info-value">{job.contact_email || '-'}</span>
              </div>
              <div className="wo-info-row">
                <span className="wo-info-label">Invoice #</span>
                <span className="wo-info-value">{job.invoice_number || '-'}</span>
              </div>
              <div className="wo-info-row">
                <span className="wo-info-label">Salesperson</span>
                <span className="wo-info-value">{job.salesperson || '-'}</span>
              </div>
            </div>

            <div className="wo-details-section">
              <h4>Dates & Production</h4>
              <div className="wo-info-row">
                <span className="wo-info-label">Order Date</span>
                <span className="wo-info-value">{formatDate(job.order_date)}</span>
              </div>
              <div className="wo-info-row">
                <span className="wo-info-label">Due Date</span>
                <span className="wo-info-value">{formatDate(job.due_date)}</span>
              </div>
              <div className="wo-info-row">
                <span className="wo-info-label">Print By</span>
                <span className="wo-info-value">{job.print_by || '-'}</span>
              </div>
              <div className="wo-info-row">
                <span className="wo-info-label">Print Date</span>
                <span className="wo-info-value">{formatDate(job.print_date)}</span>
              </div>
              <div className="wo-info-row">
                <span className="wo-info-label">Created By</span>
                <span className="wo-info-value">{job.created_by_name || '-'}</span>
              </div>
            </div>

            <div className="wo-details-section">
              <h4>Print Locations</h4>
              <div className="wo-print-locations">
                {getActivePrintLocations().length > 0 ? (
                  getActivePrintLocations().map(loc => (
                    <span key={loc.key} className="wo-print-location-badge">
                      {loc.label}
                    </span>
                  ))
                ) : (
                  <span className="wo-no-locations">None selected</span>
                )}
              </div>
            </div>

            <div className="wo-details-section">
              <h4>Totals</h4>
              <div className="wo-info-row">
                <span className="wo-info-label">Total w/Tax</span>
                <span className="wo-info-value">{formatCurrency(job.total_with_tax)}</span>
              </div>
              <div className="wo-info-row">
                <span className="wo-info-label">Deposit</span>
                <span className="wo-info-value">{formatCurrency(job.deposit)}</span>
              </div>
              <div className="wo-info-row wo-total-due-row">
                <span className="wo-info-label">Total Due</span>
                <span className="wo-info-value wo-total-due">{formatCurrency(job.total_due)}</span>
              </div>
            </div>
          </div>

          {/* Special Instructions */}
          {job.special_instructions && (
            <div className="wo-details-section wo-full-width">
              <h4>Special Instructions</h4>
              <p className="wo-instructions-text">{job.special_instructions}</p>
            </div>
          )}

          {/* Ink Details Table */}
          {job.ink_details && job.ink_details.length > 0 && (
            <div className="wo-details-section wo-full-width">
              <h4>Ink Details</h4>
              <div className="wo-table-wrapper">
                <table className="wo-details-table">
                  <thead>
                    <tr>
                      <th>Color</th>
                      <th>Instructions</th>
                      <th>Print Type 1</th>
                      <th>Print Type 2</th>
                      <th>Qty</th>
                      <th>Amount</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {job.ink_details.map((ink, index) => (
                      <tr key={index}>
                        <td>{ink.color || '-'}</td>
                        <td>{ink.instructions || '-'}</td>
                        <td>{ink.print_type_1 || '-'}</td>
                        <td>{ink.print_type_2 || '-'}</td>
                        <td>{ink.qty || '-'}</td>
                        <td>{ink.amount ? formatCurrency(ink.amount) : '-'}</td>
                        <td>{ink.total ? formatCurrency(ink.total) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Garment Details Table */}
          {job.garment_details && job.garment_details.length > 0 && (
            <div className="wo-details-section wo-full-width">
              <h4>Garment Details</h4>
              <div className="wo-table-wrapper">
                <table className="wo-details-table">
                  <thead>
                    <tr>
                      <th>Brand</th>
                      <th>SKU</th>
                      <th>Color</th>
                      <th>XS</th>
                      <th>S</th>
                      <th>M</th>
                      <th>L</th>
                      <th>XL</th>
                      <th>2XL</th>
                      <th>3XL</th>
                      <th>4XL</th>
                      <th>5XL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {job.garment_details.map((garment, index) => (
                      <tr key={index}>
                        <td>{garment.brand || '-'}</td>
                        <td>{garment.sku || '-'}</td>
                        <td>{garment.color || '-'}</td>
                        <td>{garment.xs || '-'}</td>
                        <td>{garment.s || '-'}</td>
                        <td>{garment.m || '-'}</td>
                        <td>{garment.l || '-'}</td>
                        <td>{garment.xl || '-'}</td>
                        <td>{garment.xxl || '-'}</td>
                        <td>{garment.xxxl || '-'}</td>
                        <td>{garment.xxxxl || '-'}</td>
                        <td>{garment.xxxxxl || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes */}
          {(job.notes || job.internal_notes) && (
            <div className="wo-details-section wo-full-width">
              <h4>Notes</h4>
              {job.notes && (
                <div className="wo-notes-block">
                  <span className="wo-notes-label">Customer Notes</span>
                  <p>{job.notes}</p>
                </div>
              )}
              {job.internal_notes && (
                <div className="wo-notes-block wo-internal-notes">
                  <span className="wo-notes-label">Internal Notes</span>
                  <p>{job.internal_notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Status Change */}
          {canAdmin && (
            <div className="wo-details-section wo-full-width wo-status-change">
              <h4>Change Status</h4>
              <div className="wo-status-change-row">
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="wo-status-select"
                >
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Notes (optional)"
                  value={statusChangeNotes}
                  onChange={(e) => setStatusChangeNotes(e.target.value)}
                  className="wo-status-notes"
                />
                <button
                  className="wo-btn-change-status"
                  onClick={handleStatusChange}
                  disabled={changingStatus || newStatus === job.status}
                >
                  {changingStatus ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </div>
          )}

          {/* History */}
          <div className="wo-details-section wo-full-width">
            <h4>Status History</h4>
            {loadingHistory ? (
              <div className="wo-history-loading">Loading history...</div>
            ) : history.length === 0 ? (
              <div className="wo-history-empty">No history available</div>
            ) : (
              <div className="wo-history-list">
                {history.map((entry, index) => (
                  <div key={index} className="wo-history-item">
                    <div className="wo-history-status">
                      {entry.previous_status && (
                        <>
                          <span className="wo-history-old">{entry.previous_status}</span>
                          <span className="wo-history-arrow">→</span>
                        </>
                      )}
                      <span className="wo-history-new">{entry.new_status}</span>
                    </div>
                    <div className="wo-history-meta">
                      <span>{entry.changed_by_name || 'System'}</span>
                      <span>•</span>
                      <span>{formatDateTime(entry.created_at)}</span>
                    </div>
                    {entry.notes && <div className="wo-history-notes">{entry.notes}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="wo-delete-confirm-overlay">
            <div className="wo-delete-confirm">
              <h3>Delete Work Order?</h3>
              <p>Are you sure you want to delete {job.work_order_number}? This action cannot be undone.</p>
              <div className="wo-delete-confirm-actions">
                <button onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                  Cancel
                </button>
                <button className="wo-btn-confirm-delete" onClick={handleDelete} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkOrderDetailsModal;
